import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { beURL} from '../constants/auth';
import { encodedSearchTerm } from '../utils/utils';

let baseUrl = beURL;

export const getToken = async (logout, getAccessTokenSilently) => {
  try {
    const token = await getAccessTokenSilently();
    const decoded = jwt_decode(token);

    const nowInSeconds = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp < nowInSeconds) {
      console.warn("Token expired, logging out...");
      logout();
      return null;
    }

    return token;
  } catch (err) {
    console.error("Error fetching token:", err);
    logout();
    return null;
  }
};

export const listWorkflows = async (auth, size, page, tag) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  let url = `${baseUrl}/v1/fiutur-proof/workflow-definitions?size=${size}&page=${page}`;

  if (tag && tag.length < 5) {
    tag[0]?.forEach((t) => {
      url = url + `&tags=${t.value}`;
    });
  }

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listWorkflowsFilters = async (auth) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/workflow-definitions/filters`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listWorkflow = async (auth, riid) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/workflow-definitions/${riid}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listWorkflowCounters = async (auth, riid) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/workflow-definitions/${riid}/stepCounters`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listPayloads = async (auth, riid, size, page, payloadId, ingestDateFrom, ingestDateTo, lastUpdateFrom, lastUpdateTo, status) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  let url = `${baseUrl}/v1/fiutur-proof/workflow-definitions/${riid}/payloads?size=${size}&page=${page}`;

  if (payloadId) {
    url = url + `&payloadId=${String(encodedSearchTerm(payloadId))}`;
  }
  if (ingestDateFrom) {
    url = url + `&ingestDateFrom=${ingestDateFrom}T00:00:00Z`;
  }
  if (ingestDateTo) {
    url = url + `&ingestDateTo=${ingestDateTo}T23:59:59Z`;
  }
  if (lastUpdateFrom) {
    url = url + `&lastUpdateFrom=${lastUpdateFrom}T00:00:00Z`;
  }
  if (lastUpdateTo) {
    url = url + `&lastUpdateTo=${lastUpdateTo}T23:59:59Z`;
  }
  if (status) {
    url = url + `&status=${String(encodedSearchTerm(status))}`;
  }

  return axios.get(url,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `*/*`,
        },
        maxRedirects: 0,
      }
  );
};

export const listEvents = async (auth, payload) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v2/fiutur-proof/payloads/${payload}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const getNextStep = async (auth, payload) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v2/fiutur-proof/payloads/${payload}/nextStep`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const restatePayloadEvent = async (auth, payloadId) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.post(`${baseUrl}/v2/fiutur-proof/payloads/${payloadId}/restatement-events`, null, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const addNotableEvent = async (auth, payloadId, data) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.post(`${baseUrl}/v2/fiutur-proof/payloads/${payloadId}/notable-events`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const addGeneralManualStep = async (auth, payloadId, data) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.post(`${baseUrl}/v2/fiutur-proof/payloads/${payloadId}/events`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listLedgerParties = async (auth) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/parties?size=100000&isCertifyingParty=true`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const getCurrentOrg = async (auth) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/current-org`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listMasterCertificates = async (auth, size, page, type, searchActivationDate, searchExpirationDate, searchFacilityId, searchCertificateHolderName) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  let url = `${baseUrl}/v1/fiutur-proof/master-certificates?size=${size}&page=${page}`;

  if (type) {
    url = url + `&certificateType=${String(encodedSearchTerm(type))}`;
  }
  if (searchActivationDate) {
    url = url + `&activationDate=${searchActivationDate}T00:00:00Z`;
  }
  if (searchExpirationDate) {
    url = url + `&expirationDate=${searchExpirationDate}T23:59:59Z`;
  }
  if (searchFacilityId) {
    url = url + `&facilityId=${encodedSearchTerm(searchFacilityId)}`;
  }
  if (searchCertificateHolderName) {
    url = url + `&certificateHolderPartyName=${encodedSearchTerm(searchCertificateHolderName)}`;
  }

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listMasterCertificate = async (auth, id) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/master-certificates/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const addMasterCertificate = async (auth, data) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.post(`${baseUrl}/v1/fiutur-proof/master-certificates`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const editMasterCertificate = async (auth, id, data) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.put(`${baseUrl}/v1/fiutur-proof/master-certificates/${id}`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};


export const issueMasterCertificate = async (auth, data) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.post(`${baseUrl}/v1/fiutur-proof/issued-certificates`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const deleteMasterCertificate = async (auth, id) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.delete(`${baseUrl}/v1/fiutur-proof/master-certificates/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};


export const listIssuedCertificates = async (auth, size, page, searchActivationDate, searchExpirationDate) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  let url = `${baseUrl}/v1/fiutur-proof/issued-certificates?size=${size}&page=${page}`;

  if (searchActivationDate) {
    url = url + `&activationDate=${searchActivationDate}T00:00:00Z`;
  }
  if (searchExpirationDate) {
    url = url + `&expirationDate=${searchExpirationDate}T23:59:59Z`;
  }

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const listIssuedCertificate = async (auth, id) => {
  const token = await getToken(auth.logout, auth.getAccessTokenSilently);
  if (!token) return null;

  return axios.get(`${baseUrl}/v1/fiutur-proof/issued-certificates/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};

export const getJsonSchemaFromUrl = async (url) => {

  return axios.get(url, {
    headers: {
      Accept: `*/*`,
    },
    maxRedirects: 0,
  });
};
